/**
 * Per-preset Hero + Category-Grid components for the storefront homepage.
 *
 * Six visually distinct layouts. They share data shape (categories, branding,
 * trending) but render structurally different markup — not just different
 * colours — so each preset feels like a different brand altogether.
 */
import React from "react";
import { Link } from "react-router-dom";
import ProductCard from "../../../components/store/ProductCard";

const HERO_BG = "https://images.unsplash.com/photo-1498049794561-7780e7231661?auto=format&fit=crop&w=1920&q=80";
const HERO_BG2 = "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1920&q=80";
const HERO_BG3 = "https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=1920&q=80";

const PLACEHOLDER_CATEGORY_IMG = "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=900&q=80";

function pickCategoryImg(c) {
  return c.image_url || PLACEHOLDER_CATEGORY_IMG;
}

/* ──────────────────────────────────────────────────────────────────
 * KALKI — Editorial Indian Luxury (existing layout, preserved)
 *  • Full-bleed hero, italic serif headline, 2x2 bento grid
 * ────────────────────────────────────────────────────────────────── */

/**
 * Resolve the effective hero config for the storefront homepage.
 * - `null`/missing fields fall back to the preset defaults.
 * - Empty string is preserved — admin can explicitly hide a text field
 *   (e.g. set eyebrow to "" to remove that line entirely from the hero).
 */
function resolveHero(branding, defaults) {
  const h = branding?.hero || {};
  return {
    // `||` not `??`: an empty image URL is never a meaningful value the
    // way an empty eyebrow is (that one deliberately hides the line).
    // AdminHero PUTs the whole form on every save, so image_url arrives
    // as "" whenever no image is picked — `??` preserved that and the
    // hero rendered <img src="">, which browsers resolve against the
    // page URL and paint as a broken image. Same treatment cta_link
    // already gets below.
    image_url: h.image_url || defaults.image_url,
    eyebrow: h.eyebrow ?? defaults.eyebrow,
    title_line_1: h.title_line_1 ?? defaults.title_line_1,
    title_line_2: h.title_line_2 ?? defaults.title_line_2,
    subtitle: h.subtitle ?? defaults.subtitle,
    cta_label: h.cta_label ?? defaults.cta_label,
    cta_link: h.cta_link || defaults.cta_link, // links: empty also falls back (no broken nav)
    secondary_cta_label: h.secondary_cta_label ?? defaults.secondary_cta_label,
    secondary_cta_link: h.secondary_cta_link || defaults.secondary_cta_link,
    text_align: h.text_align || "left",          // 'left' | 'center' | 'right'
    vertical_align: h.vertical_align || "center", // 'top' | 'center' | 'bottom'
  };
}

/** Tailwind class lookup tables — kept tiny + colocated with `resolveHero`. */
const TEXT_ALIGN_CLASS = {
  left:   "text-left items-start",
  center: "text-center items-center",
  right:  "text-right items-end",
};
const VERTICAL_ALIGN_CLASS = {
  top:    "justify-start pt-20",
  center: "justify-center",
  bottom: "justify-end pb-20",
};
export function heroAlignClasses(hero) {
  return [
    TEXT_ALIGN_CLASS[hero.text_align] || TEXT_ALIGN_CLASS.left,
    VERTICAL_ALIGN_CLASS[hero.vertical_align] || VERTICAL_ALIGN_CLASS.center,
  ].join(" ");
}

export function KalkiHero({ branding }) {
  const hero = resolveHero(branding, {
    image_url: HERO_BG,
    eyebrow: branding?.tagline || "Mega Sale · Up to 70% off · Limited Time",
    title_line_1: "Everything you need.",
    title_line_2: "Delivered fast.",
    subtitle: "Your one-stop marketplace for electronics, home, beauty and more — from verified sellers across India.",
    cta_label: "Shop now",
    cta_link: "/shop",
    secondary_cta_label: "Sell with us",
    secondary_cta_link: "/seller/onboard",
  });
  return (
    <section className="relative h-[85vh] md:h-[92vh] overflow-hidden" data-testid="hero-kalki">
      <img src={hero.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/60" />
      <div className={`relative z-10 h-full flex flex-col text-white px-6 ${heroAlignClasses(hero)}`}>
        {hero.eyebrow && <div className="overline text-white/90 mb-6">{hero.eyebrow}</div>}
        {(hero.title_line_1 || hero.title_line_2) && (
          <h1 className="font-display text-5xl sm:text-7xl lg:text-[110px] leading-[0.95] max-w-5xl font-light tracking-tighter">
            {hero.title_line_1}{hero.title_line_1 && hero.title_line_2 && <br />}
            {hero.title_line_2 && <em className="italic text-[hsl(var(--kb-accent))]">{hero.title_line_2}</em>}
          </h1>
        )}
        {hero.subtitle && <p className="max-w-xl mt-8 text-white/80 text-lg leading-relaxed">{hero.subtitle}</p>}
        {(hero.cta_label || hero.secondary_cta_label) && (
          <div className="flex gap-4 mt-10">
            {hero.cta_label && <Link to={hero.cta_link} className="kb-btn-primary" data-testid="hero-shop-btn">{hero.cta_label}</Link>}
            {hero.secondary_cta_label && (
              <Link to={hero.secondary_cta_link} className="kb-btn-outline border-white text-white hover:bg-white/10" data-testid="hero-sell-btn">{hero.secondary_cta_label}</Link>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export function KalkiCategoryGrid({ categories }) {
  return (
    <section className="px-6 md:px-12 lg:px-24 py-20" data-testid="cat-kalki">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="overline mb-3">Shop by Department</div>
          <h2 className="font-display text-4xl lg:text-5xl">Everything,<br />in one marketplace.</h2>
        </div>
        <Link to="/shop" className="hidden md:inline text-sm uppercase tracking-widest underline">View all →</Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {categories.filter((c) => !c.parent_id).slice(0, 4).map((c) => (
          <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`home-category-${c.slug}`}
                className="relative overflow-hidden group aspect-[3/4]">
            <img src={pickCategoryImg(c)} alt={c.name} className="w-full h-full object-cover kb-product-img" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-4 left-4 text-white">
              <div className="overline text-white/80">Shop</div>
              <div className="font-display text-2xl md:text-3xl">{c.name}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * MYNTRA — Bright e-commerce: small banner + circular category icons + product carousel
 * ────────────────────────────────────────────────────────────────── */

export function MyntraHero({ branding }) {
  // Tile 1 is the admin-controlled hero: it renders the image, eyebrow,
  // headline and CTA saved on /admin/branding/hero. Tiles 2-3 stay as the
  // preset's editorial fillers. Before this, the whole strip was hardcoded
  // to HERO_BG/HERO_BG3/HERO_BG2, so a saved hero image never appeared.
  const hero = resolveHero(branding, {
    image_url: HERO_BG,
    eyebrow: "MIN 60% OFF",
    title_line_1: "Mega Deals",
    title_line_2: "",
    subtitle: "",
    cta_label: "Shop now",
    cta_link: "/shop",
    secondary_cta_label: "",
    secondary_cta_link: "/shop",
  });
  const tiles = [
    { img: hero.image_url, overline: hero.eyebrow, title: hero.title_line_1, to: hero.cta_link, cta: hero.cta_label },
    { img: HERO_BG3, overline: "TRENDING NOW", title: "Trending", to: "/shop", cta: "Shop now" },
    { img: HERO_BG2, overline: "DESIGNER EDITS", title: "Top Brands", to: "/shop", cta: "Shop now" },
  ];
  return (
    <section className="relative" data-testid="hero-myntra">
      <div className="bg-gradient-to-r from-[hsl(var(--kb-primary))] to-[hsl(var(--kb-accent))] py-3 text-white text-center text-sm font-semibold tracking-wide">
        {branding?.tagline || "FLAT 50–70% OFF · NEW LAUNCHES LIVE"} · USE CODE WELCOME10
      </div>
      <div className="grid md:grid-cols-3 gap-1">
        {tiles.map((t, i) => (
          <Link key={i} to={t.to} className="relative aspect-[16/10] overflow-hidden group" data-testid={`hero-myntra-tile-${i}`}>
            <img src={t.img} alt="" className="w-full h-full object-cover kb-product-img" />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition" />
            <div className="absolute inset-x-6 bottom-6 text-white">
              {t.overline && <div className="text-xs font-bold tracking-widest opacity-80">{t.overline}</div>}
              {t.title && <div className="font-display text-3xl mt-2 leading-tight">{t.title}</div>}
              {t.cta && <div className="mt-3 inline-block bg-white text-[hsl(var(--kb-primary))] px-4 py-2 text-xs font-bold uppercase">{t.cta}</div>}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function MyntraCategoryGrid({ categories }) {
  const cats = categories.filter((c) => !c.parent_id).slice(0, 8);
  return (
    <section className="px-4 md:px-8 py-10 bg-[hsl(var(--kb-surface-alt))]" data-testid="cat-myntra">
      <div className="text-center mb-6">
        <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-wide">Shop by Category</h2>
      </div>
      <div className="flex md:grid md:grid-cols-8 gap-4 overflow-x-auto kb-scroll-hide pb-3 -mx-4 px-4 md:mx-0 md:px-0">
        {cats.map((c) => (
          <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`home-category-${c.slug}`}
                className="flex-shrink-0 w-24 md:w-auto flex flex-col items-center text-center group">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-2 border-transparent group-hover:border-[hsl(var(--kb-primary))] transition">
              <img src={pickCategoryImg(c)} alt={c.name} className="w-full h-full object-cover kb-product-img" />
            </div>
            <div className="text-xs font-semibold mt-2 uppercase">{c.name}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * AJIO — Sporty modern: split-screen hero, bold sans-serif, horizontal scroll chips
 * ────────────────────────────────────────────────────────────────── */

export function AjioHero({ branding }) {
  const hero = resolveHero(branding, {
    image_url: HERO_BG,
    eyebrow: branding?.tagline || "DROP 04 · IN STORE NOW",
    title_line_1: "Move",
    title_line_2: "different.",
    subtitle: "The new collection is live. 200+ new launches from 18+ sellers. Be the first to grab them.",
    cta_label: "Shop the drop →",
    cta_link: "/shop",
    secondary_cta_label: "Watch reels",
    secondary_cta_link: "/reels",
  });
  return (
    <section className="grid md:grid-cols-2 min-h-[80vh] bg-[hsl(var(--kb-primary))] text-white" data-testid="hero-ajio">
      <div className="flex flex-col justify-center p-10 md:p-20 order-2 md:order-1">
        <div className="text-xs font-bold tracking-[0.3em] text-[hsl(var(--kb-accent))] mb-6">{hero.eyebrow}</div>
        <h1 className="font-display text-6xl md:text-7xl lg:text-8xl leading-[0.95] font-extrabold uppercase">
          {hero.title_line_1}<br />
          <span className="text-[hsl(var(--kb-accent))]">{hero.title_line_2}</span>
        </h1>
        <p className="mt-8 text-white/70 max-w-md leading-relaxed">{hero.subtitle}</p>
        <div className="flex gap-3 mt-10">
          <Link to={hero.cta_link} className="bg-[hsl(var(--kb-accent))] text-black px-8 py-4 uppercase text-sm font-bold tracking-wider hover:opacity-90 transition" data-testid="hero-shop-btn">{hero.cta_label}</Link>
          {hero.secondary_cta_label && (
            <Link to={hero.secondary_cta_link} className="border border-white px-8 py-4 uppercase text-sm font-bold tracking-wider hover:bg-white hover:text-black transition" data-testid="hero-sell-btn">{hero.secondary_cta_label}</Link>
          )}
        </div>
      </div>
      <div className="relative aspect-square md:aspect-auto order-1 md:order-2">
        <img src={hero.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
      </div>
    </section>
  );
}

export function AjioCategoryGrid({ categories }) {
  const cats = categories.filter((c) => !c.parent_id).slice(0, 6);
  return (
    <section className="py-16" data-testid="cat-ajio">
      <div className="px-6 md:px-12 mb-8 flex items-end justify-between flex-wrap gap-4">
        <h2 className="font-display text-4xl md:text-5xl font-extrabold uppercase">Edits</h2>
        <Link to="/shop" className="text-xs font-bold uppercase tracking-widest underline">All edits →</Link>
      </div>
      <div className="flex gap-3 overflow-x-auto kb-scroll-hide px-6 md:px-12 pb-3">
        {cats.map((c, i) => (
          <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`home-category-${c.slug}`}
                className={`flex-shrink-0 relative overflow-hidden group ${i % 3 === 0 ? "w-[420px] aspect-[4/5]" : "w-[280px] aspect-[3/4]"}`}>
            <img src={pickCategoryImg(c)} alt={c.name} className="w-full h-full object-cover kb-product-img" />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition" />
            <div className="absolute inset-0 flex flex-col justify-end p-5 text-white">
              <div className="text-xs font-bold tracking-widest text-[hsl(var(--kb-accent))]">EDIT 0{i + 1}</div>
              <div className="font-display text-2xl md:text-3xl font-extrabold uppercase">{c.name}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * MANGO — Pure monochrome editorial: fullscreen hero, generous whitespace, masonry categories
 * ────────────────────────────────────────────────────────────────── */

export function MangoHero({ branding }) {
  const hero = resolveHero(branding, {
    image_url: HERO_BG,
    eyebrow: branding?.tagline || "Spring · Summer 26",
    title_line_1: "The art",
    title_line_2: "of less.",
    subtitle: "",
    cta_label: "Discover the collection",
    cta_link: "/shop",
    secondary_cta_label: null,
    secondary_cta_link: null,
  });
  return (
    <section className="relative h-screen overflow-hidden bg-white" data-testid="hero-mango">
      <img src={hero.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale" />
      <div className="absolute inset-0 bg-white/10" />
      <div className="relative z-10 h-full flex flex-col items-end justify-end text-right text-white p-10 md:p-20">
        <div className="text-xs uppercase tracking-[0.4em] mb-6 opacity-80">{hero.eyebrow}</div>
        <h1 className="font-display text-7xl md:text-8xl lg:text-[140px] leading-[0.85] font-extrabold tracking-tighter max-w-3xl">
          {hero.title_line_1}<br />{hero.title_line_2}
        </h1>
        <Link to={hero.cta_link} className="mt-12 border-b border-white text-sm uppercase tracking-[0.3em] pb-2 hover:tracking-[0.4em] transition-all" data-testid="hero-shop-btn">{hero.cta_label}</Link>
      </div>
    </section>
  );
}

export function MangoCategoryGrid({ categories }) {
  const cats = categories.filter((c) => !c.parent_id).slice(0, 5);
  if (!cats.length) return null;
  return (
    <section className="px-6 md:px-12 lg:px-32 py-32" data-testid="cat-mango">
      <div className="text-center mb-20">
        <div className="text-xs uppercase tracking-[0.4em] text-[hsl(var(--kb-muted))] mb-4">Selection</div>
        <h2 className="font-display text-5xl md:text-7xl font-light">Curated essentials.</h2>
      </div>
      <div className="grid grid-cols-12 gap-4 md:gap-6">
        {cats.slice(0, 5).map((c, i) => {
          const layouts = [
            "col-span-12 md:col-span-7 aspect-[16/9]",
            "col-span-12 md:col-span-5 aspect-[4/5]",
            "col-span-6 md:col-span-4 aspect-square",
            "col-span-6 md:col-span-4 aspect-square",
            "col-span-12 md:col-span-4 aspect-square",
          ];
          return (
            <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`home-category-${c.slug}`}
                  className={`relative overflow-hidden group ${layouts[i]}`}>
              <img src={pickCategoryImg(c)} alt={c.name} className="w-full h-full object-cover kb-product-img grayscale group-hover:grayscale-0 transition duration-700" />
              <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/60 to-transparent text-white">
                <div className="font-display text-3xl md:text-4xl">{c.name}</div>
                <div className="text-xs uppercase tracking-[0.3em] opacity-80 mt-1">View collection</div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * BIBA — Warm Indian ethnic: festive overlay hero, rounded cream cards
 * ────────────────────────────────────────────────────────────────── */

export function BibaHero({ branding }) {
  const hero = resolveHero(branding, {
    image_url: HERO_BG,
    eyebrow: branding?.tagline || "Top Deals · This Week",
    title_line_1: "Shop more,",
    title_line_2: "save more.",
    subtitle: "Hand-picked top picks across electronics, home and beauty — all verified by us, delivered fast to your door.",
    cta_label: "Shop deals",
    cta_link: "/shop",
    secondary_cta_label: "Our sellers",
    secondary_cta_link: "/seller/onboard",
  });
  return (
    <section className="relative h-[85vh] overflow-hidden bg-[hsl(var(--kb-surface-alt))]" data-testid="hero-biba">
      <img src={hero.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-l from-[hsl(var(--kb-primary))]/85 via-[hsl(var(--kb-primary))]/40 to-transparent" />
      <div className="relative z-10 h-full flex items-center justify-end px-6 md:px-20">
        <div className="max-w-xl text-right text-white">
          <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--kb-accent))] mb-4">{hero.eyebrow}</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-tight">
            {hero.title_line_1}<br /><em className="italic text-[hsl(var(--kb-accent))]">{hero.title_line_2}</em>
          </h1>
          <p className="mt-6 text-white/85 leading-relaxed">{hero.subtitle}</p>
          <div className="flex gap-3 mt-8 justify-end flex-wrap">
            <Link to={hero.cta_link} className="bg-white text-[hsl(var(--kb-primary))] px-7 py-3 uppercase text-xs font-bold tracking-wider rounded-full hover:bg-[hsl(var(--kb-accent))] hover:text-white transition" data-testid="hero-shop-btn">{hero.cta_label}</Link>
            {hero.secondary_cta_label && (
              <Link to={hero.secondary_cta_link} className="border border-white text-white px-7 py-3 uppercase text-xs font-bold tracking-wider rounded-full hover:bg-white/10 transition" data-testid="hero-sell-btn">{hero.secondary_cta_label}</Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export function BibaCategoryGrid({ categories }) {
  const cats = categories.filter((c) => !c.parent_id).slice(0, 6);
  return (
    <section className="px-6 md:px-12 py-20 bg-[hsl(var(--kb-bg))]" data-testid="cat-biba">
      <div className="text-center mb-12">
        <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--kb-primary))] mb-2">Shop by occasion</div>
        <h2 className="font-display text-4xl md:text-5xl">From <em className="italic text-[hsl(var(--kb-primary))]">mehendi</em> to <em className="italic text-[hsl(var(--kb-primary))]">reception</em>.</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
        {cats.map((c) => (
          <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`home-category-${c.slug}`}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[hsl(var(--kb-border))] group hover:shadow-md transition">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={pickCategoryImg(c)} alt={c.name} className="w-full h-full object-cover kb-product-img" />
            </div>
            <div className="p-4 text-center">
              <div className="font-display text-xl text-[hsl(var(--kb-primary))]">{c.name}</div>
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--kb-muted))] mt-1">Explore →</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * NEERUS — Couture jewel: dark ornate hero, gold accent borders, 4-col luxury grid
 * ────────────────────────────────────────────────────────────────── */

export function NeerusHero({ branding }) {
  const hero = resolveHero(branding, {
    image_url: HERO_BG,
    eyebrow: branding?.tagline || "Premium Selection · 2026",
    title_line_1: "The Premium",
    title_line_2: "Marketplace",
    subtitle: "Hand-picked. Authenticated. Curated by our team and delivered with the care your purchase deserves.",
    cta_label: "Discover Now",
    cta_link: "/shop",
    secondary_cta_label: "Sell with us",
    secondary_cta_link: "/seller/onboard",
  });
  return (
    <section className="relative h-[90vh] overflow-hidden bg-[hsl(var(--kb-primary))]" data-testid="hero-neerus">
      <img src={hero.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--kb-primary))]/70 via-[hsl(var(--kb-primary))]/40 to-[hsl(var(--kb-primary))]" />
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-6">
        <div className="w-px h-16 bg-[hsl(var(--kb-accent))] mb-8" />
        <div className="text-xs tracking-[0.5em] uppercase text-[hsl(var(--kb-accent))] mb-6">{hero.eyebrow}</div>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-tight uppercase tracking-wide" style={{ letterSpacing: "0.04em" }}>
          {hero.title_line_1}<br />
          <span className="text-[hsl(var(--kb-accent))]">{hero.title_line_2}</span>
        </h1>
        <div className="w-12 h-px bg-[hsl(var(--kb-accent))] my-8" />
        <p className="max-w-lg text-white/80 leading-relaxed">{hero.subtitle}</p>
        <div className="flex gap-4 mt-10">
          <Link to={hero.cta_link} className="bg-[hsl(var(--kb-accent))] text-[hsl(var(--kb-primary))] px-10 py-4 uppercase text-xs font-semibold tracking-[0.3em] hover:opacity-90" data-testid="hero-shop-btn">{hero.cta_label}</Link>
          {hero.secondary_cta_label && (
            <Link to={hero.secondary_cta_link} className="border border-[hsl(var(--kb-accent))] text-[hsl(var(--kb-accent))] px-10 py-4 uppercase text-xs font-semibold tracking-[0.3em] hover:bg-[hsl(var(--kb-accent))]/10" data-testid="hero-sell-btn">{hero.secondary_cta_label}</Link>
          )}
        </div>
      </div>
    </section>
  );
}

export function NeerusCategoryGrid({ categories }) {
  const cats = categories.filter((c) => !c.parent_id).slice(0, 4);
  return (
    <section className="px-6 md:px-12 py-24 bg-[hsl(var(--kb-bg))]" data-testid="cat-neerus">
      <div className="text-center mb-16">
        <div className="w-12 h-px bg-[hsl(var(--kb-accent))] mx-auto mb-6" />
        <div className="text-xs uppercase tracking-[0.4em] text-[hsl(var(--kb-primary))] mb-3">Top Picks</div>
        <h2 className="font-display text-4xl md:text-5xl uppercase tracking-wider" style={{ letterSpacing: "0.05em" }}>
          Hand-picked, <em className="italic text-[hsl(var(--kb-accent))]">curated.</em>
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
        {cats.map((c) => (
          <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`home-category-${c.slug}`}
                className="relative overflow-hidden group aspect-[3/4] border border-[hsl(var(--kb-accent))]/40 hover:border-[hsl(var(--kb-accent))] transition">
            <img src={pickCategoryImg(c)} alt={c.name} className="w-full h-full object-cover kb-product-img" />
            <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--kb-primary))] via-transparent to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-5 text-white text-center">
              <div className="w-8 h-px bg-[hsl(var(--kb-accent))] mx-auto mb-3" />
              <div className="font-display text-xl md:text-2xl uppercase tracking-widest">{c.name}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * FLIPKART — Marketplace classic: blue header tint, big coupon banner,
 * top-row category icons with yellow accent ring.
 * ────────────────────────────────────────────────────────────────── */

export function FlipkartHero({ branding }) {
  const hero = resolveHero(branding, {
    image_url: HERO_BG,
    eyebrow: branding?.tagline || "EXCLUSIVE COUPON FOR YOU!",
    title_line_1: "FLAT 10%",
    title_line_2: "OFF",
    subtitle: "Already applied · Up to ₹100 · No code needed",
    cta_label: "Shop the deal",
    cta_link: "/shop",
    secondary_cta_label: "Browse categories",
    secondary_cta_link: "/categories",
  });
  return (
    <section className="relative px-6 py-10 md:py-14 bg-gradient-to-b from-[hsl(var(--kb-primary))]/15 to-[hsl(var(--kb-bg))]" data-testid="hero-flipkart">
      <div className="max-w-5xl mx-auto bg-[hsl(var(--kb-accent))]/20 border border-dashed border-[hsl(var(--kb-primary))]/30 rounded-2xl overflow-hidden grid md:grid-cols-2 items-center gap-6 p-6 md:p-10">
        <div>
          <div className="text-xs font-bold tracking-wider text-[hsl(var(--kb-primary))] uppercase mb-3">{hero.eyebrow}</div>
          <h1 className="font-display text-5xl md:text-7xl font-extrabold leading-[0.95] text-[hsl(var(--kb-primary))]">
            {hero.title_line_1}
            <span className="text-[hsl(var(--kb-text))] text-3xl md:text-4xl ml-2">{hero.title_line_2}</span>
          </h1>
          <p className="mt-4 text-sm md:text-base text-[hsl(var(--kb-text))]/80">{hero.subtitle}</p>
          <div className="flex gap-3 mt-6 flex-wrap">
            <Link to={hero.cta_link} className="bg-[hsl(var(--kb-primary))] text-white px-6 py-3 rounded-lg font-bold text-sm hover:opacity-90 transition" data-testid="hero-shop-btn">{hero.cta_label}</Link>
            {hero.secondary_cta_label && (
              <Link to={hero.secondary_cta_link} className="bg-[hsl(var(--kb-accent))] text-[hsl(var(--kb-text))] px-6 py-3 rounded-lg font-bold text-sm hover:opacity-90 transition" data-testid="hero-sell-btn">{hero.secondary_cta_label}</Link>
            )}
          </div>
        </div>
        <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-white">
          <img src={hero.image_url} alt="" className="w-full h-full object-cover" />
        </div>
      </div>
    </section>
  );
}

export function FlipkartCategoryGrid({ categories }) {
  const cats = categories.filter((c) => !c.parent_id).slice(0, 8);
  return (
    <section className="px-4 md:px-12 py-8" data-testid="cat-flipkart">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl md:text-2xl font-bold text-[hsl(var(--kb-text))]">Shop by category</h2>
        <Link to="/shop" className="text-xs uppercase tracking-wider text-[hsl(var(--kb-primary))] font-semibold">View all</Link>
      </div>
      <div className="flex md:grid md:grid-cols-6 gap-4 md:gap-6 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
        {cats.map((c) => (
          <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`home-category-${c.slug}`}
                className="flex flex-col items-center text-center group min-w-[88px] snap-start">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-[hsl(var(--kb-primary))]/10 border-2 border-transparent group-hover:border-[hsl(var(--kb-primary))] transition overflow-hidden flex items-center justify-center">
              <img src={pickCategoryImg(c)} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="text-xs md:text-sm font-semibold text-[hsl(var(--kb-text))] mt-2 line-clamp-2 group-hover:text-[hsl(var(--kb-primary))]">{c.name}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * AMAZON BAZAAR — Punchy deals: coral header overlap, lime green
 * category circles, yellow CTA pills, dense product grid.
 * ────────────────────────────────────────────────────────────────── */

export function AmazonHero({ branding }) {
  const hero = resolveHero(branding, {
    image_url: HERO_BG,
    eyebrow: branding?.tagline || "₹100 STORE · CRAZY LOW PRICES",
    title_line_1: "Search products",
    title_line_2: "for crazy low prices",
    subtitle: "Top wireless picks · Phone covers · Earphones · Daily deals at ₹99 and below.",
    cta_label: "Shop deals",
    cta_link: "/shop",
    secondary_cta_label: "See all deals",
    secondary_cta_link: "/shop?sort=discount",
  });
  // `resolveHero` returns image_url but this layout previously never
  // rendered it, so a hero image saved in admin was silently discarded.
  // Only an explicitly-set image paints — with no override the band keeps
  // its original flat brand colour.
  const heroImg = (branding?.hero?.image_url || "").trim();
  return (
    <section className="relative overflow-hidden bg-[hsl(var(--kb-primary))] text-white px-6 py-10 md:py-14" data-testid="hero-amazon">
      {heroImg && (
        <>
          <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="hero-amazon-bg" />
          <div className="absolute inset-0 bg-[hsl(var(--kb-primary))]/80" />
        </>
      )}
      <div className="relative max-w-5xl mx-auto">
        <div className="text-center">
          <div className="text-[10px] md:text-xs font-bold tracking-[0.25em] uppercase text-[hsl(var(--kb-accent))] mb-3">{hero.eyebrow}</div>
          <h1 className="font-display text-3xl md:text-5xl font-extrabold leading-tight">
            {hero.title_line_1}
            <br />
            <span className="text-[hsl(var(--kb-accent))]">{hero.title_line_2}</span>
          </h1>
          <p className="mt-4 text-sm md:text-base text-white/85 max-w-xl mx-auto">{hero.subtitle}</p>
        </div>
        <div className="mt-6 max-w-2xl mx-auto bg-white rounded-xl flex items-center px-4 py-3 shadow-lg">
          <span className="text-[hsl(var(--kb-primary))] mr-2">🔍</span>
          <input className="flex-1 min-w-0 bg-transparent text-[hsl(var(--kb-text))] placeholder:text-[hsl(var(--kb-muted))] text-sm focus:outline-none" placeholder="Search products for crazy low prices" />
        </div>
        <div className="flex gap-3 mt-6 justify-center flex-wrap">
          <Link to={hero.cta_link} className="bg-[hsl(var(--kb-accent))] text-[hsl(var(--kb-text))] px-6 py-3 rounded-full font-bold text-sm hover:opacity-90 transition" data-testid="hero-shop-btn">{hero.cta_label}</Link>
          {hero.secondary_cta_label && (
            <Link to={hero.secondary_cta_link} className="border border-white/40 text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-white/10 transition" data-testid="hero-sell-btn">{hero.secondary_cta_label}</Link>
          )}
        </div>
      </div>
    </section>
  );
}

export function AmazonCategoryGrid({ categories }) {
  const cats = categories.filter((c) => !c.parent_id).slice(0, 8);
  return (
    <section className="px-4 md:px-12 py-8 bg-white" data-testid="cat-amazon">
      <div className="flex md:grid md:grid-cols-6 gap-4 md:gap-6 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
        {cats.map((c) => (
          <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`home-category-${c.slug}`}
                className="flex flex-col items-center text-center group min-w-[88px] snap-start">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[hsl(140_70%_55%)] p-1 group-hover:scale-105 transition-transform">
              <div className="w-full h-full rounded-full bg-white overflow-hidden flex items-center justify-center">
                <img src={pickCategoryImg(c)} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
            <div className="text-xs md:text-sm font-bold text-[hsl(var(--kb-text))] mt-2 line-clamp-2">{c.name}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Trending product grid variants per preset (subtle tweaks)
 * ────────────────────────────────────────────────────────────────── */

export function TrendingGrid({ products, preset, loading, error }) {
  const wrapClass = {
    myntra: "px-4 md:px-8 py-10 bg-white",
    ajio: "px-6 md:px-12 py-16",
    mango: "px-6 md:px-12 lg:px-32 py-32 bg-[hsl(var(--kb-bg))]",
    biba: "px-6 md:px-12 py-16 bg-[hsl(var(--kb-surface-alt))]",
    neerus: "px-6 md:px-12 py-24",
    kalki: "px-6 md:px-12 lg:px-24 py-20",
  }[preset] || "px-6 md:px-12 lg:px-24 py-20";

  const headers = {
    myntra: { eyebrow: "Trending now", title: "Bestsellers this week", view: "Shop more →" },
    ajio: { eyebrow: "JUST IN", title: "NEW LAUNCHES.", view: "All launches →" },
    mango: { eyebrow: "New arrivals", title: "This week's launches.", view: "View all" },
    biba: { eyebrow: "Just arrived", title: "Fresh on the shelf", view: "See everything →" },
    neerus: { eyebrow: "New launches", title: "Hand-picked. Ready to ship.", view: "Shop all →" },
    kalki: { eyebrow: "Just Arrived", title: "New launches this week.", view: "See everything →" },
  }[preset] || {};

  const cols = preset === "myntra" || preset === "neerus" ? "md:grid-cols-4"
    : preset === "mango" ? "md:grid-cols-3"
    : preset === "flipkart" || preset === "amazon" ? "md:grid-cols-3 lg:grid-cols-4"
    : "md:grid-cols-4";

  return (
    <section className={wrapClass}>
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="overline mb-3">{headers.eyebrow}</div>
          <h2 className="font-display text-4xl lg:text-5xl">{headers.title}</h2>
        </div>
        <Link to="/shop" className="text-sm uppercase tracking-widest underline">{headers.view}</Link>
      </div>
      {error && (
        <div className="mb-6 border border-red-300 bg-red-50 text-red-800 p-3 text-sm" data-testid="home-error">{error}</div>
      )}
      {loading ? (
        <div className={`grid grid-cols-2 ${cols} gap-6 md:gap-8`}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="py-10 text-[hsl(var(--kb-muted))]">No products available yet.</div>
      ) : (
        <div className={`grid grid-cols-2 ${cols} gap-6 md:gap-8`}>
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </section>
  );
}
